import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BASE_URL } from "../utils";
import { useTheme } from "../contexts/ThemeContext";
import AppDataGrid from "../components/AppDataGrid";
import { BOROUGH_OPTIONS, confidencePill, formatDate, safeText } from "./acrisLegacyUtils";

export default function AcrisLegacyDashboard() {
  const [stats, setStats] = useState(null);
  const [boroughs, setBoroughs] = useState([]);
  const [recentRows, setRecentRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentLoading, setRecentLoading] = useState(false);
  const [error, setError] = useState(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const cardStyle = isDark
    ? "border-slate-700 bg-slate-900/80 text-slate-100"
    : "border-slate-200 bg-white text-slate-900";

  const StatCard = ({ label, value, tone }) => (
    <div className={`relative overflow-hidden rounded-2xl border p-4 ${cardStyle}`}>
      <div className={`absolute inset-0 opacity-50 ${tone}`} aria-hidden />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)]">{label}</div>
        <div className="mt-1 text-2xl font-extrabold">{value ?? 0}</div>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${BASE_URL}/api/acris-legacy/stats`);
        setStats(res.data?.stats || {});
        setBoroughs(res.data?.borough_breakdown || []);
      } catch (err) {
        console.error("AcrisLegacy stats error", err);
        setError("Failed to load ACRIS Legacy stats.");
      } finally {
        setLoading(false);
      }
    };

    const fetchRecent = async () => {
      setRecentLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/acris-legacy/leads`, {
          params: { page: 1, limit: 6, sortBy: "obituary_date_of_death", sortDir: "desc" },
        });
        setRecentRows(res.data?.data || []);
      } catch (err) {
        console.error("AcrisLegacy recent error", err);
      } finally {
        setRecentLoading(false);
      }
    };

    fetchStats();
    fetchRecent();
  }, []);

  const recentColumns = useMemo(
    () => [
      {
        headerName: "Name",
        field: "full_name",
        minWidth: 170,
        flex: 1,
        sortable: false,
        cellRenderer: (p) => {
          const id = p && p.data ? p.data.id : null;
          if (!id) return <span className="block truncate">{safeText(p.value)}</span>;
          return (
            <Link
              to={`/acris-legacy/leads/${id}`}
              target="_blank"
              rel="noreferrer"
              className="block w-full truncate text-left text-indigo-600 hover:underline"
              title={p.value || ""}
              onClick={(e) => e.stopPropagation()}
            >
              {safeText(p.value)}
            </Link>
          );
        },
      },
      {
        headerName: "Date of Death",
        field: "obituary_date_of_death",
        width: 130,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Address",
        field: "property_full_address",
        minWidth: 260,
        flex: 1,
        cellRenderer: (p) => (
          <span className="block truncate" title={p.value || ""}>
            {safeText(p.value)}
          </span>
        ),
      },
      { headerName: "Borough", field: "property_borough_name", width: 120 },
      {
        headerName: "Confidence",
        field: "match_confidence",
        width: 130,
        cellRenderer: (p) => <span className={confidencePill(p.value)}>{safeText(p.value, "NA")}</span>,
      },
    ],
    []
  );

  return (
    <div className="p-2 md:p-3 lg:p-4 space-y-4">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">
        <div className="absolute inset-0 opacity-80 bg-gradient-to-r from-indigo-100/60 via-cyan-100/50 to-transparent" aria-hidden />
        <div className="relative px-4 py-4 md:px-5 md:py-4 lg:px-6 lg:py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-500 flex items-center justify-center text-[15px] font-bold text-white shadow-lg shadow-indigo-500/25">
                AL
              </div>
              <div className="leading-tight">
                <p className="page-kicker">Web Platform</p>
                <h1 className="page-title">ACRIS Legacy Dashboard</h1>
                <p className="page-subtitle">
                  Read-only ACRIS Legacy probate intelligence.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="pill px-3 py-1 text-[11px] font-semibold">Acris Legacy</span>
              <span className="flex items-center gap-2 rounded-full px-3 py-1 text-[11px] border border-slate-200 bg-slate-100 text-slate-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden />
                <span className="font-semibold">Live</span>
                <span className="text-slate-500">data</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="surface-card rounded-2xl p-3 text-sm text-rose-600">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <StatCard label="Total Records" value={loading ? "..." : stats?.total_records} tone="bg-emerald-50" />
        <StatCard label="Probate Leads" value={loading ? "..." : stats?.probate_leads} tone="bg-indigo-50" />
        <StatCard label="Transferred" value={loading ? "..." : stats?.transferred_records} tone="bg-rose-50" />
        <StatCard label="PDFs Downloaded" value={loading ? "..." : stats?.pdfs_downloaded} tone="bg-amber-50" />
        <StatCard label="Strong Matches" value={loading ? "..." : stats?.strong_matches} tone="bg-sky-50" />
        <StatCard label="Weak Matches" value={loading ? "..." : stats?.weak_matches} tone="bg-slate-50" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="surface-card rounded-2xl p-4 bg-gradient-to-br from-indigo-50/80 via-white to-cyan-50/70">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold">Borough Breakdown</div>
              <div className="text-xs text-[var(--fg-muted)]">Verified records by borough</div>
            </div>
            <span className="chip-muted">{stats?.borough_count || 0} boroughs</span>
          </div>

          <div className="space-y-2">
            {(boroughs.length ? boroughs : BOROUGH_OPTIONS.map((b) => ({ borough: b, total: 0 }))).map(
              (item) => {
                const total = Number(item.total || 0);
                const max = Math.max(...boroughs.map((b) => Number(b.total || 0)), 1);
                const width = Math.min(100, Math.round((total / max) * 100));
                return (
                  <div key={item.borough} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{item.borough || "NA"}</span>
                      <span className="text-[var(--fg-muted)]">{total}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>

        <div className="surface-card rounded-2xl p-4 xl:col-span-2 bg-gradient-to-br from-sky-50/70 via-white to-indigo-50/60">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold">Recent Probate Leads</div>
              <div className="text-xs text-[var(--fg-muted)]">Latest verified owner matches</div>
            </div>
            <Link className="ui-btn ui-btn-ghost" to="/acris-legacy/leads" target="_blank" rel="noreferrer">
              View all
            </Link>
          </div>
          <div className="rounded-2xl border border-[var(--border)] overflow-hidden">
            <AppDataGrid
              rowData={recentRows}
              columnDefs={recentColumns}
              loading={recentLoading}
              height={260}
              minWidth={0}
              className="ag-acris-grid"
              defaultColDef={{
                resizable: true,
                sortable: false,
                filter: true,
                suppressMenu: false,
                cellClass: "text-sm",
                headerClass: "text-left",
                cellStyle: {
                  userSelect: "text",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
